export function isFeatureEnabled(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return false;
  return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
}

// K-1.1 (Operator UX 2.0): «Рабочий центр менеджера» — режим по умолчанию.
// Rollback-окно: VITE_V2_SALES_UX_LEGACY=1 возвращает старый лендинг;
// явный VITE_V2_SALES_UX_NEXT сохраняет прежнюю принудительную семантику.
export function isV2SalesUxNextEnabled(env: Record<string, unknown> = import.meta.env): boolean {
  if (isFeatureEnabled(env.VITE_V2_SALES_UX_LEGACY)) return false;
  const explicit = env.VITE_V2_SALES_UX_NEXT;
  if (typeof explicit === 'boolean') return explicit;
  if (typeof explicit === 'string' && explicit.trim() !== '') return isFeatureEnabled(explicit);
  return true;
}

// K-1.6: в сайдбаре 4 финансовых пункта; полный реестр — только за явным флагом.
export function isV2FinanceNavFullEnabled(env: Record<string, unknown> = import.meta.env): boolean {
  return isFeatureEnabled(env.VITE_V2_FINANCE_NAV_FULL);
}

// FLOW[51286]: новый ledger — основной /v2/accounting. Откат не требует
// изменения маршрутов: явные `0`/`false` возвращают прежний дашборд.
export function isV2FinanceLedgerEnabled(env: Record<string, unknown> = import.meta.env): boolean {
  const explicit = env.VITE_V2_FINANCE_LEDGER;
  if (typeof explicit === 'boolean') return explicit;
  if (typeof explicit === 'string' && explicit.trim() !== '') return isFeatureEnabled(explicit);
  return true;
}

// /v2/proto/* доступны в dev всегда; в прод-сборке — только при явном VITE_V2_PROTO_ROUTES.
export function isV2ProtoRoutesEnabled(env: Record<string, unknown> = import.meta.env): boolean {
  return isFeatureEnabled(env.DEV) || isFeatureEnabled(env.VITE_V2_PROTO_ROUTES);
}

// FLOW[44005]: Sales Manager UX 3.0 / W3 — форма заказа считает позиции через
// единый item-composer (модель W2). Default OFF до закрытия parity-гейта;
// включение по умолчанию — W12. localStorage-оверрайд нужен parity-спеке и
// ручной проверке на живом dev-сервере без его перезапуска (vite env
// читается только при старте).
export function isV2SharedItemComposerEnabled(
  env: Record<string, unknown> = import.meta.env,
): boolean {
  try {
    const override = globalThis.localStorage?.getItem('v2.sharedItemComposer');
    if (override !== null && override !== undefined) return isFeatureEnabled(override);
  } catch {
    // приватный режим/SSR — падаем на env
  }
  // FLOW[44036]: W12 — default ON; откат: VITE_V2_SHARED_ITEM_COMPOSER_LEGACY=1 (rollback-окно K-1.1-паттерна)
  if (isFeatureEnabled(env.VITE_V2_SHARED_ITEM_COMPOSER_LEGACY)) return false;
  const explicit = env.VITE_V2_SHARED_ITEM_COMPOSER;
  if (typeof explicit === 'boolean') return explicit;
  if (typeof explicit === 'string' && explicit.trim() !== '') return isFeatureEnabled(explicit);
  return true;
}

// FLOW[44009]: Sales Manager UX 3.0 / W4 (E-12) — review-гейт создания заказа:
// одна primary «Создать заказ» + селектор исхода (CTA success-страницы, не
// цепочка записей). Default OFF до W12; localStorage-оверрайд для e2e/ручной
// проверки без рестарта dev-сервера.
export function isV2OrderOutcomeGateEnabled(
  env: Record<string, unknown> = import.meta.env,
): boolean {
  try {
    const override = globalThis.localStorage?.getItem('v2.orderOutcomeGate');
    if (override !== null && override !== undefined) return isFeatureEnabled(override);
  } catch {
    // приватный режим/SSR — падаем на env
  }
  // FLOW[44036]: W12 — default ON; откат: VITE_V2_ORDER_OUTCOME_GATE_LEGACY=1 (rollback-окно K-1.1-паттерна)
  if (isFeatureEnabled(env.VITE_V2_ORDER_OUTCOME_GATE_LEGACY)) return false;
  const explicit = env.VITE_V2_ORDER_OUTCOME_GATE;
  if (typeof explicit === 'boolean') return explicit;
  if (typeof explicit === 'string' && explicit.trim() !== '') return isFeatureEnabled(explicit);
  return true;
}

// FLOW[44018]: Sales Manager UX 3.0 / W7 — быстрые карточки согласованных
// вариантов в мастере КП (заполнение item-формы через существующий
// applySalesVariantToForm; добавление позиции — штатным путём с calculate-v2).
// Default OFF до W12.
export function isV2QuoteComposerEnabled(
  env: Record<string, unknown> = import.meta.env,
): boolean {
  try {
    const override = globalThis.localStorage?.getItem('v2.quoteComposer');
    if (override !== null && override !== undefined) return isFeatureEnabled(override);
  } catch {
    // приватный режим/SSR — падаем на env
  }
  // FLOW[44036]: W12 — default ON; откат: VITE_V2_QUOTE_COMPOSER_LEGACY=1 (rollback-окно K-1.1-паттерна)
  if (isFeatureEnabled(env.VITE_V2_QUOTE_COMPOSER_LEGACY)) return false;
  const explicit = env.VITE_V2_QUOTE_COMPOSER;
  if (typeof explicit === 'boolean') return explicit;
  if (typeof explicit === 'string' && explicit.trim() !== '') return isFeatureEnabled(explicit);
  return true;
}
